import React, { PropsWithChildren, useCallback, useEffect, useState } from 'react';
import { Box, BoxProps, Container, ContainerProps } from '@mui/material';
import { ControlsStringAssets, MessagesStringAssets, PageAndViewTitleStringAssets } from '../../../../assets/stringAssets';
import { enumAlertType } from '../../../enums';
import { IHyperLink } from '../../../../dataObjects/models/digitalMedia/HyperLink';
import { typeUniqueId } from '../../../../dataObjects/types';
import { IStoreState } from '../../../../uiMiddleware-redux/store/IStoreState';
import { enumBeaconType, enumCollectionViewType, enumWorkflowState } from '../../../../dataObjects/enums';
import TwoButtonAcceptanceDialog from '../../../dialogs/TwoButtonAcceptanceDialog/TwoButtonAcceptanceDialog';
import { Beacon } from '../../../../dataObjects/models/alerts/Beacon';
import { useReduxWorkflowState } from '../../../customHooks';
import ActivityIndicatorDialog from '../../../dialogs/ActivityIndicatorDialog/ActivityIndicatorDialog';
import { IActivityIndicatorData } from '../../../dialogs/ActivityIndicatorData';
import { AlertInfo, IAlertInfo } from '../../../../dataObjects/models/alerts/AlertInfo';
import { useGetCollectionViewType } from '../../../customHooks/useGetCollectionViewType';
import GenericToggleViewContainer from '../../GenericToggleViewContainer/GenericToggleViewContainer';
import { HyperLinksCardGridView } from '../HyperLinksCardGridView/HyperLinksCardGridView';
import HyperLinksListView from '../HyperLinksListView/HyperLinksListView';
import { styled } from '@mui/styles';
import { IHyperLinkViewModel } from '../../../../dataObjects/viewModels/hyperLinkViewModel';
import { useAppDispatch } from '../../../../uiMiddleware-redux/store/configureStore';
import { hyperLinkDeleteRequest, hyperLinkDeleteStatusChange } from '../../../../uiMiddleware-redux/slices/hyperLink/hyperLinkDeleteStatusSlice';
import { beaconChange } from '../../../../uiMiddleware-redux/slices/beacon/beaconSlice';
import { alertInfoChange } from '../../../../uiMiddleware-redux/slices/alertInfo/alertInfoSlice';


/*** Using the Material UI Emotion Styling library, declare 'styled' instances for each area/object. 
 *** NOTE: These must be declared outside of the React Functional Component to ensure that the styled 
 *** objects will be properly rendered within the DOM. 
 ***/

// a styled Box (equivalent to a <div>), providing an area padding at the top of the view
const StyledBoxForPaddingAtopView = styled((props: BoxProps) => (
  <Box
    {...props}
  />
))(({ theme }) => ({
  marginTop: theme.spacing(2),
}));

// a styled Container for the View Container
const StyledContainerForViewContainer = styled((props: ContainerProps) => (
  <Container
    {...props}
  />
))(({ theme }) => ({
  flexGrow: 1,
  paddingLeft: 0,
  paddingRight: 0,
}));

export interface IHyperLinksViewProps extends PropsWithChildren<unknown> {
  parentId?: typeUniqueId; // the parent DigitalMedia for the HyperLinks
  hyperLinksToDisplay: Array<IHyperLinkViewModel>;
  allowAddingNewItems: boolean;
  hyperLinksDataLoading: boolean;
}

const HyperLinksView: React.FC<IHyperLinksViewProps> = (props: IHyperLinksViewProps) => {

  HyperLinksView.displayName = 'HyperLinks View';

  // whether to display console logs (displayConsoleLogs && console.log statements)
  const displayConsoleLogs: boolean = false;

  const { parentId, hyperLinksToDisplay, allowAddingNewItems, hyperLinksDataLoading } = props;

  // call custom hook to get the current view type, so the value can be used to initialize the View Type state variable
  const collectionViewType: enumCollectionViewType = useGetCollectionViewType();

  // the HyperLinks associcated with the current Digital Media
  // const [hyperLinks, setHyperLinks] = useState<Array<IHyperLink>>(hyperLinksToDisplay);
  const [hyperLinkViewModels, setHyperLinkViewModels] = useState<Array<IHyperLinkViewModel>>(hyperLinksToDisplay);
  // whether data is currently loading
  const [dataLoading, setDataLoading] = useState<boolean>(hyperLinksDataLoading);
  // if user is deleting a HyperLink, the HyperLink to delete
  const [hyperLinkToDelete, setHyperLinkToDelete] = useState<IHyperLink | undefined>(undefined);
  // whether to show the Delete Confirmation dialog
  const [showDeleteConfirmationDlg, setShowDeleteConfirmationDlg] = useState<boolean>(false);

  // for dispatching redux actions
  const dispatch = useAppDispatch();

  /**
   * @function resetDeleteHyperLinkStatus Resets the Delete status in Redux state
   */
  const resetDeleteHyperLinkStatus: () => void = useCallback(() => {
    dispatch(hyperLinkDeleteStatusChange(null));
  }, [dispatch])

  // use a custom hook to evaluate the redux state for the deleteHyperLink workflow and any deleteHyperLink error
  const { workflowState: deleteHyperLinkStatus, errorState: deleteHyperLinkFailure } = useReduxWorkflowState((state: IStoreState) => state.deleteHyperLinkStatus, (state: IStoreState) => state.deleteHyperLinkFailure);

  useEffect(() => {
    if (hyperLinkViewModels !== hyperLinksToDisplay) {
      setHyperLinkViewModels(hyperLinksToDisplay);
    }
  }, [hyperLinksToDisplay, hyperLinkViewModels]);

  useEffect(() => {
    if (dataLoading !== hyperLinksDataLoading) {
      setDataLoading(hyperLinksDataLoading);
    }
  }, [dataLoading, hyperLinksDataLoading]);

  /**
   * @method handleHyperLinkDelete Handles requests to delete a Channel
   * @param hyperLink The HyperLink to be deleted
   */
  async function handleHyperLinkDelete(hyperLink: IHyperLink): Promise<void> {
    // set the hyperLink to be deleted into local state
    setHyperLinkToDelete(hyperLink);

    // set flag to show the delete confirmation dialog
    setShowDeleteConfirmationDlg(true);
  }

  function handleHyperLinkDeleteConfirmed(): void {
    // using the hyperLink object to be deleted from the local state, dispatch an action to delete the hyperLink object
    if (hyperLinkToDelete !== undefined) {
      dispatch(hyperLinkDeleteRequest(hyperLinkToDelete));
    }

    // call method to reset the hyperLink delete local state details
    hyperLinkDeleteReset();
  }

  function handleHyperLinkDeleteCanceled(): void {
    // call method to reset the hyperLink delete local state details
    hyperLinkDeleteReset();
  }

  function hyperLinkDeleteReset() {
    // reset the hyperLink to be deleted to 'undefined' in local state, since we're done with the object
    setHyperLinkToDelete(undefined);

    // set flag to hide the delete confirmation dialog
    setShowDeleteConfirmationDlg(false);
  }


  // data to indicate whether an activity indicator should be displayed and, if so, the message
  let activityIndicatorData: IActivityIndicatorData = {
    showActivityIndicator: false,
    activityIndicatorMessage: ''
  }

  // data to indicate whether an alert should be displayed
  let alertInfo: IAlertInfo | undefined = undefined;

  // indicates whether the object has been successfully deleted
  const [successfullyDeletedObject, setSuccessfullyDeletedObject] = useState<boolean>(false);

  // define an effect that will process whenever the successfullyCompletedPage state changes
  useEffect(() => {
    if (successfullyDeletedObject) {
      // dispatch an action to reset the deleteTopicStatus
      resetDeleteHyperLinkStatus();

      // notify user via a Beacon notification that the audioLink has been deleted
      dispatch(beaconChange(new Beacon(undefined, enumBeaconType.Success, MessagesStringAssets.hyperLink_DeleteConfirmationHeader, MessagesStringAssets.hyperLink_DeleteSuccess)));

      // reset the successfullyDeletedObject flag
      setSuccessfullyDeletedObject(false);
    }
  }, [successfullyDeletedObject, dispatch, resetDeleteHyperLinkStatus]);

  if (dataLoading) {
    // display an activity indicator
    activityIndicatorData.showActivityIndicator = true;
    activityIndicatorData.activityIndicatorMessage = MessagesStringAssets.hyperLinks_FetchingRequested;
  } else {
    // if the workflow is in the midst of performing deletion of a hyperLink, prepare an alert message fragment to display progress message(s)
    if (deleteHyperLinkStatus !== undefined && deleteHyperLinkStatus !== null) {
      if (deleteHyperLinkStatus === enumWorkflowState.Requested || deleteHyperLinkStatus === enumWorkflowState.InProgress) {
        // display an activity indicator
        activityIndicatorData.showActivityIndicator = true;
        activityIndicatorData.activityIndicatorMessage = MessagesStringAssets.hyperLink_DeleteRequested;
      } else if (deleteHyperLinkStatus === enumWorkflowState.Success) {
        // if the successfullyDeletedObject is curently in a 'false' state, we'll want the flag to indicate that the deletion was a success
        if (!successfullyDeletedObject) {
          // set flag to indicate that the object has been successfully deleted, which will force a re-render to allow for cleanup and user notification
          setSuccessfullyDeletedObject(true);
        }
      } else if (deleteHyperLinkStatus === enumWorkflowState.Failure) {
        // display an alert message
        // alertFragment = generateJsxAlertMessageFragment(MessagesStringAssets.hyperLink_DeleteFailure, enumJsxAlertMessageDisplayVariants.Failure, false, deleteHyperLinkFailureMessage, MessagesStringAssets.substitutionKeyword);
        let failureMessage = 'Unknown error';
        if (deleteHyperLinkFailure && deleteHyperLinkFailure instanceof Error) {
          failureMessage = deleteHyperLinkFailure.message;
        }
        alertInfo = new AlertInfo(true, enumAlertType.Error, failureMessage);
        dispatch(alertInfoChange(alertInfo));
      }
    }
  }


  // Establish the text to be displayed on the '+ New' button
  let addNewButtonText: string = '';
  if (allowAddingNewItems) {
    addNewButtonText = ControlsStringAssets.hyperLinkNew;
  }


  return (
    <>
      <StyledBoxForPaddingAtopView />
      <GenericToggleViewContainer
        viewTitle={PageAndViewTitleStringAssets.viewTitle_HyperLinks}
        addNewButtonText={addNewButtonText}
        urlForAddNewButtonNavigation={`/${parentId}/hyperLink`}
      >

        {/* Container for embedded data view */}
        <StyledContainerForViewContainer >

          {/* If there are HyperLinks to display and the current view selection is CardsGridView, display the HyperLinksCardGridView */}
          {hyperLinkViewModels &&
            (collectionViewType === enumCollectionViewType.CardsGridView) &&
            <HyperLinksCardGridView hyperLinkViewModels={hyperLinkViewModels} onDelete={handleHyperLinkDelete} />
          }

          {/* If there are HyperLinks to display and the current view selection is ListView, display the HyperLinksListView */}
          {hyperLinkViewModels &&
            (collectionViewType === enumCollectionViewType.ListView) &&
            <HyperLinksListView hyperLinkViewModels={hyperLinkViewModels} onDelete={handleHyperLinkDelete} />
          }
        </StyledContainerForViewContainer>

        {/* Delete HyperLink Confirmation Dialog */}
        <TwoButtonAcceptanceDialog
          showDialog={showDeleteConfirmationDlg}
          headerText={MessagesStringAssets.hyperLink_DeleteConfirmationHeader}
          bodyText={MessagesStringAssets.hyperLink_DeleteConfirmation}
          acceptanceButtonText={ControlsStringAssets.confirmButtonText}
          nonAcceptanceButtonText={ControlsStringAssets.cancelButtonText}
          onAcceptance={handleHyperLinkDeleteConfirmed}
          onNonAcceptance={handleHyperLinkDeleteCanceled}
        />

        {activityIndicatorData.showActivityIndicator &&
          <ActivityIndicatorDialog activityLabel={activityIndicatorData.activityIndicatorMessage} />
        }

      </GenericToggleViewContainer>
    </>
  );
}

export default HyperLinksView;
